import { MutationFunction, useLazyQuery } from '@apollo/client';
import debounce from 'debounce-promise';
import { useMemo } from 'react';
import {
	AsyncComboboxAutocomplete,
	alephToast,
	NotificationToast,
	NotificationType,
	handleErrorNotification, isReddit, isSpotify,
} from '@aleph/shared-components';
import {
	InviteUserToGroupMutation,
	InviteUserToGroupMutationVariables,
	RedditUserForGroupAutocompleteDocument,
	RedditUserForGroupQueryUsers,
	UserGroupRole,
} from 'wb-reddit/graphql/generated';
import { HandleUpdateTeamRow, TeamRow } from 'wb-reddit/app/routes/teams/container';
import { parseTeamOwnerFromFromQueryUser, User } from 'wb-reddit/app/routes/teams/utils';
import { ComboboxAutocompleteOption } from 'wb-reddit/app/common/components/comboboxAutocompleteOption';
import { Trans, useTranslation } from 'react-i18next';
import {
	AuthGraphUsersListDocument, UserGroupAccessRole,
} from 'wb-spotify/graphql/authgraph/generated';
import { captureException } from '@sentry/react';

interface IProps {
	owner: User
	row: TeamRow
	setFieldValue: (field: string, value: any) => void
	inviteUserToGroup: MutationFunction<InviteUserToGroupMutation, InviteUserToGroupMutationVariables>
	updateRow?: HandleUpdateTeamRow
}

export default function TeamUsersAutocomplete({
	row, setFieldValue, inviteUserToGroup, owner, updateRow,
}: IProps) {
	const { t } = useTranslation();

	const [searchUsers] = useLazyQuery(
		isReddit() ? RedditUserForGroupAutocompleteDocument : AuthGraphUsersListDocument,
	);
	const groupUsersEmails = useMemo(
		// @ts-ignore
		() => row?.userGroupAccess?.map((item) => item?.user?.userEmail) ?? [],
		// @ts-ignore
		row?.userGroupAccess,
	);

	const searchUsersDebounced = useMemo(() => debounce(searchUsers, 500), []);

	const loadUsers = async (search: string) => {
		if (search.length < 3) {
			return [];
		}

		if (isReddit()) {
			const ownerEmail = owner?.userEmail?.split?.('@')?.[1];
			const [searchName, searchEmail] = search.split('@') ?? [];

			const { data, error } = await searchUsersDebounced({
				variables: {
					// @ts-ignore
					ownerEmail: `${searchName}@${searchEmail === ownerEmail ? searchEmail : ''}`,
				},
			});

			if (error) {
				captureException(error);

				handleErrorNotification(
					error,
					t(
						're: team users autocomplete error',
						'Something went wrong while searching for users',
					),
				);

				return [];
			}

			return (data?.users ?? [])
				// @ts-ignore
				.filter(user => !row.users?.some(currUser => currUser.user?.userId === user.userId))
				.map((user) => ({
					...user,
					user,
					userGroupUserRole:          UserGroupRole.Member,
					userGroupPendingInviteHash: null,
				}));
		}

		const { data, error } = await searchUsersDebounced({
			variables: {
				// @ts-ignore
				search: '**',
				email:  `*${search}*`,
			},
		});

		if (error) {
			captureException(error);

			handleErrorNotification(
				error,
				t(
					're: team users autocomplete error',
					'Something went wrong while searching for users',
				),
			);

			return [];
		}

		// @ts-ignore
		const itms = (data?.users?.items ?? [])
		// @ts-ignore
			.filter(user => !groupUsersEmails.some(currUser => currUser === user?.userEmail))
			.map(user => ({
				...user,
				label:               `${user?.userName} ${user?.userSurname ?? ''}`,
				userGroupAccessRole: UserGroupAccessRole.Member,
			}));

		// @ts-ignore
		return itms;
	};

	const onUserInvited = async (user: RedditUserForGroupQueryUsers) => {
		if (row.userGroupId) {
			const { errors: userInviteErrors, data } = await inviteUserToGroup({
				variables: {
					userId:      user?.userId ?? '',
					userGroupId: row.userGroupId ?? '',
				},
			});

			if (userInviteErrors) {
				alephToast(
					<NotificationToast
						type={NotificationType.Error}
						title={
							t(
								're: something went wrong while inviting user',
								'Something went wrong while inviting user',
							)
						}
					/>,
				);

				return;
			}

			alephToast(
				<NotificationToast
					type={NotificationType.Success}
					title={
						<Trans
							i18nKey="re: user invited to team"
							defaults="User <strong>{{userFullName}}</strong> successfully invited to <strong>{{userGroupName}}</strong>"
							values={{
								// @ts-ignore
								userFullName:  isReddit() ? user?.userFullName : `${user?.userName} ${user?.userSurname ?? ''}`,
								userGroupName: row.userGroupName,
							}}
						>
							{/* @ts-ignore */}
							User <strong>{isReddit() ? user?.userFullName : `${user?.userName} ${user?.userSurname ?? ''}`}</strong>
							&nbsp;successfully invited to <strong>{row.userGroupName}</strong>
						</Trans>
					}
				/>,
			);

			if (isReddit()) {
				setFieldValue('users', data?.linkUsersToUserGroup?.users ?? []);

				updateRow?.({
					row: {
						...row,
						users: (data?.linkUsersToUserGroup?.users ?? []).map(u => ({
							...u,
							user: parseTeamOwnerFromFromQueryUser(u.user),
						})),
					},
				});
			}

			if (isSpotify()) {
				setFieldValue(
					'userGroupAccess',
					// @ts-ignore
					data?.addUserToUserGroup?.userGroup?.userGroupAccess,
				);

				updateRow?.({
					row: {
						...row,
						// @ts-ignore
						userGroupAccess: data?.addUserToUserGroup?.userGroup?.userGroupAccess,
					},
				});
			}

			return;
		}

		if (isReddit()) {
			setFieldValue('users', [...(row.users ?? []), user]);
		} else {
			setFieldValue('users', [...(row.users ?? []), {
				user,
				// @ts-ignore
				userGroupAccessRole: user.userGroupAccessRole,
			}]);
		}
	};

	return (
		<AsyncComboboxAutocomplete
			inputProps={{
				placeholder: t('re: add users', 'Add users...'),
			}}
			size="large"
			loadOptions={loadUsers}
			hideSufixChevrons
			renderOption={({ option, searchQuery, onSelected }) => (
				<ComboboxAutocompleteOption
					option={option}
					// @ts-ignore
					onOptionClick={onUserInvited}
					onSelected={onSelected}
					searchQuery={searchQuery}
				/>
			)}
			NoOptionsComponent={() => (
				<div className='max-w-[25vw] w-fit'>
					<strong>{t('re: full email', 'Use full email address to find a user')}</strong>
				</div>
			)}
		/>
	);
}
